/**
 * Subscription Model - Data model for user subscriptions
 * 
 * MIGRATION STATUS: Now uses Pinia (not Vuex)
 * 
 * CHANGES FROM VUEX VERSION:
 * - Extends AbstractPiniaModel instead of AbstractModel
 * - Imports useSubscriptionsStore from Pinia instead of using getters()
 * - Static methods access Pinia store via piniaStore() instead of getters()
 * - Data is persisted separately from Vuex (see pinia/stores/subscriptions.js)
 * 
 * ARCHITECTURE:
 * - Instance methods work on a single subscription object (wrapped in a model)
 * - Static methods interface with the Pinia store for state management
 * - Maintains the same public API as the Vuex version for backward compatibility
 * 
 * STORAGE:
 * Subscriptions are stored in Pinia state:
 * - localStorage key: "${userEmail}@pinia"
 * - Structure: { subscriptionsStore: { allSubscriptions, currentSubscription } }
 */

import AbstractPiniaModel from '../models/AbstractPiniaModel';
import { useSubscriptionsStore } from '../pinia/stores/subscriptions';
import {SubscriptionModel,Professions,MainCategories} from 'Models';
import moment from 'moment/min/moment.min';


/**
 * Subscription Model Class
 * 
 * Represents a single user subscription with date calculations and status checks
 * 
 * STATIC USAGE (accessing state via Pinia):
 * - Subscription.getAll()                           → Get all subscriptions
 * - Subscription.getCurrent()                       → Get current subscription
 * - Subscription.setAll(subscriptions)              → Replace all subscriptions
 * - Subscription.setCurrentByProfessionID(id)       → Set current by profession
 * 
 * INSTANCE USAGE (working with a single subscription):
 * - new Subscription(subscriptionData)              → Create model wrapper
 * - subscription.isActive()                         → Check if subscription is valid today
 * - subscription.isInExtensionPeriod()              → Check if in extension window
 * - subscription.isPastEndDate()                    → Check if expired
 */
export class Subscription extends AbstractPiniaModel {
    //#region Instance Code
        constructor(subscription) { super( subscription ); }

        id()                    { return Number(this.source.id); }
        professionID()          { return Number(this.source.profession_id); }
        professionTitle()       { return 'profession' in this.source  ? this.source.profession.titel :  Professions.getByID(this.professionID()).title() }
        extensionCount()        { return Number(this.getByAliases(['anzahl_verlaengerungen'], 0)) }
        startDate()             { return moment(this.source.start_datum); }
        endDate()               { return moment(this.source.end_datum); }
        startDateForExtension() {
            const d = Professions.getSelected().extensionStartDeltaInDays() ? Professions.getSelected().extensionStartDeltaInDays() : 15;
            let date = moment(this.endDate())
                date.subtract(d, 'days')
            return date
        }
        endDateForExtension(profession=null)   {
            const monthsToAddForFinalDate = profession ? profession.extensionEndDeltaInMonths()
                                                       : Professions.getSelected().extensionEndDeltaInMonths() ? Professions.getSelected().extensionEndDeltaInMonths() : 1

            let date = moment(this.endDate())
                date.add(monthsToAddForFinalDate, 'months')
            return date
        }
        isPastEndDate()         { return moment().isAfter( this.endDate(), 'day' ) }
        isEndDate()             { return moment().isSame( this.endDate(), 'day' ) }
        isInExtensionPeriod(profession=null)   {
            let startExtensionPeriod    = this.startDateForExtension()

            let endExtensionPeriod      = this.endDateForExtension(profession)

            return moment().isBetween(startExtensionPeriod, endExtensionPeriod, 'day', '[]')
        }
        isActive()      { return moment().isSameOrBefore(this.endDate(), 'day') }
        isUpcoming()    { return moment().isBefore(this.startDate(), 'day') }
        isUsable()      { return moment().isBetween(this.startDate(), this.endDate(), 'day', '[]') }
        expired()       { return moment().isAfter(this.endDateForExtension(), 'day') }
        extensionCost() { return this.getByAliases(['verlaengerungs_preis'], 0) }


    //#endregion Instance Code

    //#region Static Methods
        /**
         * Returns the Pinia subscriptions store
         * This is the single source of truth for subscription state
         * Called by AbstractPiniaModel.dispatch() and getters
         * @returns {Store} Pinia subscriptions store instance
         */
        static piniaStore() {
            return useSubscriptionsStore()
        }

        /**
         * Create a new Subscription model instance from raw data
         * @param {Object} subscriptionSource - Raw subscription object
         * @returns {Subscription} Wrapped model instance
         */
        static create(subscriptionSource)   { return new Subscription(subscriptionSource); }
        static createFromId(id)             { return this.create(this.getters().getSubscriptionById(id))}

        static isActiveFor(professionID)    {
            const sub = Subscription.getAll().find(s=>s.professionID() === professionID)
            return (!sub) ? false : sub.isActive()
        }

        static isExpiredFor(professionID)    {
            const sub = Subscription.getAll().find(s=>s.professionID() === professionID)
            return (!sub) ? false : sub.expired()
        }

        /**
         * Get the currently selected subscription as a model instance
         * Returns the subscription stored in Pinia's currentSubscription state
         * @returns {Subscription} Current subscription wrapped in model
         */
        static getCurrent()                 { return this.create(this.piniaStore().currentSubscription); }
        
        /**
         * Get all subscriptions as model instances
         * Maps the Pinia store's allSubscriptions array to Subscription models
         * @returns {Array<Subscription>} All subscriptions wrapped in models
         */
        static getAll()                     { return this.piniaStore().allSubscriptions.map(s => this.create(s)) }

        /**
         * Set the current subscription by profession ID
         * Finds and sets the subscription matching the given profession ID
         * Persists to localStorage via Pinia action
         * @param {number} id - Profession ID to find
         */
        static setCurrentByProfessionID(id) { return this.dispatch('setCurrentSubscriptionByProfessionID', id); }
        
        /**
         * Replace all subscriptions in the store
         * Accepts Subscription models or raw data objects
         * Extracts source data and persists to localStorage via Pinia action
         * @param {Array} subscriptions - Array of Subscription models or raw objects
         */
        static setAll(subscriptions)        { this.dispatch('setAllSubscriptions', subscriptions.map(s=>s.getSource())); }
    //#endregion Static Methods
}
